import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { Paths, ProjectPathParams } from '../../app/router/app.router';
import { ListPageHeader } from '../../ui/components/pure/page-headers/list/list-page-header.component';
import { useQueryGetProject } from '../../core/queries/projects.queries';
import { WindowTitle } from '../../core/window-title/window-title.component';
import { EventHistory } from './components/event-history/event-history.component';
import { LockerUtilisation } from './components/locker-utilisation/locker-utilisation.component';
import { RetailStatusCountGrid } from './components/status-count/retail-status-count-grid/retail-status-count-grid.component';
import { TimeInLocker } from './components/time-in-locker/time-in-locker.component';
import { HistoricStatsContextProvider } from './context/use-dashboard-stats.context';
import { makeDashboardHeaderProps } from './logic/dashboard-props.map';
import { useDisplayWorkflowTypes } from '../../hooks/use-display-workflow-types.hook';
import { DeliveryStatusCountGrid } from './components/status-count/delivery-status-count-grid/delivery-status-count-grid.component';
import { KnownProjectDtoDisplayWorkflowTypesItem } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCurrentProject } from '../../core/context/project.context';
import { BookingFlowType } from '../../ui/enums/booking-flow-type.enum';
import { InventoryStatusCountGrid } from './components/status-count/inventory-status-count-grid/inventory-status-count-grid.component';
import { useManualDashboardRefresh } from './hooks/useManualDashboardRefresh';

export function DashboardPage({ params }: { params: ProjectPathParams<Paths.dashboard> }) {
  const project = useQueryGetProject(params.projectID);
  const [displayWorkflowTypes] = useDisplayWorkflowTypes(params.projectID);
  const onRefresh = useManualDashboardRefresh();
  return (
    <HistoricStatsContextProvider projectID={params.projectID}>
      <DashboardLayout
        params={params}
        projectName={project.data?.name}
        displayWorkflowTypes={displayWorkflowTypes}
        onRefresh={onRefresh}
      />
    </HistoricStatsContextProvider>
  );
}

/* This function component is exported only for use in Storybook */
export function DashboardLayout({
  projectName,
  params,
  displayWorkflowTypes,
  onRefresh
}: {
  projectName?: string;
  params: ProjectPathParams<Paths.dashboard>;
  displayWorkflowTypes: string[];
  onRefresh?: () => void;
}) {
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.only('sm'));
  const isXS = useMediaQuery(theme.breakpoints.only('xs'));

  const headerProps = makeDashboardHeaderProps(projectName, onRefresh);

  // The Current Delivery Stats Grid is displayed by default to prevent the page being jumpy when loading the workflow types
  const showCurrentDeliveryStats = !displayWorkflowTypes.includes(
    KnownProjectDtoDisplayWorkflowTypesItem.WorkflowRetail
  );

  const { bookingFlowType, isLoading } = useCurrentProject();

  // Wait for the project to load before deciding which booking flow version of the merged
  // stats grid to show, to avoid the page being jumpy / flashing the wrong flow.
  const showMergedDeliveryStats =
    !isLoading && displayWorkflowTypes.includes(KnownProjectDtoDisplayWorkflowTypesItem.WorkflowRetail);

  const MergedComponent =
    bookingFlowType === BookingFlowType.INVENTORY ? InventoryStatusCountGrid : RetailStatusCountGrid;

  return (
    <div>
      <WindowTitle pageTitle="Dashboard" detail={projectName} />
      <ListPageHeader {...headerProps} />
      <Grid container spacing={3}>
        {showCurrentDeliveryStats ? (
          <Grid container>
            <DeliveryStatusCountGrid params={params} />
          </Grid>
        ) : null}
        {showMergedDeliveryStats ? (
          <Grid container>
            <MergedComponent params={params} />
          </Grid>
        ) : null}
        <Grid container>
          <Grid container sx={{ flexDirection: isSM || isXS ? 'column' : 'row' }} wrap="nowrap" spacing={3}>
            <Grid
              container
              size={{
                xs: 'auto',
                md: 8
              }}
            >
              <EventHistory />
            </Grid>
            <Grid
              container
              size={{
                xs: 'auto',
                md: 4
              }}
            >
              <Grid container sx={{ flexDirection: isSM ? 'row' : 'column' }} spacing={3}>
                <Grid
                  size={{
                    xs: 'auto',
                    sm: 6,
                    md: 'auto'
                  }}
                >
                  <TimeInLocker />
                </Grid>
                <Grid
                  size={{
                    xs: 'auto',
                    sm: 6,
                    md: 'auto'
                  }}
                >
                  <LockerUtilisation />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}
